/**
 * @fileoverview The sections of the mock Sales Dashboard.
 * @description Builds each section of GET /sales/dashboard from the stores the owning pages already
 * read, so a dashboard figure is the figure its page shows (Confluence 3.5.11.40): Active Clients
 * from the client projection, Backlog from the Sales Pipeline's prospects (accepted, unarchived,
 * with no open target, plus those pending approval), Achievement and Needs Attention from the
 * target performance model for the financial year to date, My Page from the My Page summary, Sales
 * Team from the caller's cohort and User Management from the roster. Months approximate the
 * backend's rule: a target is due in the month its period ends and counts in the month it was
 * achieved.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { resolveScopedUserIds } from '@/features/sales/lib/sales-hierarchy.utils'

import { listClients } from './active-clients-mock-guard'
import { clientStatusOf, hrAdminStateOf } from './active-clients-mock-projection'
import { hrAdminOf } from './active-clients-mock-state'
import { indiaToday, summaryReply, targetStatusOf } from './my-page-mock.service'
import {
  monthsOf,
  pendingStatusOf,
  periodOf,
  scopeOf,
  targetsFor,
} from './sales-performance-mock-model'
import { listSalesRoster } from './sales-roster-mock-store'
import { listTargets } from './targets-mock-store'
import { listTenants } from './tenants-mock-store'

import type { MyPageSummary } from '@/features/sales/types/my-page.types'
import type {
  DashboardAchievement,
  DashboardActiveClients,
  DashboardBacklog,
  DashboardMonth,
  DashboardMyPage,
  DashboardNeedsAttention,
  DashboardSalesTeam,
  DashboardScope,
  DashboardUserManagement,
} from '@/features/sales/types/sales-dashboard.types'
import type { SalesUserRecord } from '@/features/sales/types/tenant.types'
import type { AxiosRequestConfig } from 'axios'

/** @description The backend's default Lowest Month threshold, as a percentage. */
const LOWEST_MONTH_PERCENT = 60

/** @description The performance scope kinds as the dashboard names them. */
const SCOPE_OF_KIND: Record<'ORGANISATION' | 'TEAM' | 'SELF', DashboardScope> = {
  ORGANISATION: 'ALL',
  TEAM: 'TEAM',
  SELF: 'OWN',
}

/** @description The most Needs Attention rows the backend returns. */
const ATTENTION_CEILING = 100

/**
 * @description The scope of the caller's own results: everyone for a Super Admin or Sales Head, the
 * team for a Sales Manager, themself for a Sales Executive.
 * @param {SalesUserRecord} caller - The signed-in user.
 * @returns {DashboardScope} The scope.
 */
export function scopeKindOf(caller: SalesUserRecord): DashboardScope {
  return SCOPE_OF_KIND[scopeOf(caller).kind]
}

/**
 * @description The Active Clients section: every active client, the same for every Sales role.
 * @returns {DashboardActiveClients} The totals.
 */
export function activeClientsSection(): DashboardActiveClients {
  const active = listClients().filter((tenant) => clientStatusOf(tenant) === 'ACTIVE')
  const live = active.filter((tenant) => hrAdminStateOf(hrAdminOf(tenant)) === 'ACTIVE').length
  return {
    scope: 'ALL',
    total: active.length,
    live,
    onboarding: active.length - live,
    branches: active.reduce((sum, tenant) => sum + tenant.branches, 0),
  }
}

/**
 * @description The Backlog section: the Sales Pipeline's Total Prospects, org-wide.
 * @returns {DashboardBacklog} The total, its Hot, Warm and Cold split and the pending subset.
 */
export function backlogSection(): DashboardBacklog {
  const withOpenTarget = new Set(
    listTargets()
      .filter((target) => targetStatusOf(target) === 'OPEN')
      .map((target) => target.tenantId),
  )
  const pending = listTenants({ status: 'Prospect', approval: 'pending' })
  const accepted = listTenants({ status: 'Prospect', archived: false }).filter(
    (tenant) => !withOpenTarget.has(tenant.id),
  )
  const backlog = [
    ...new Map([...accepted, ...pending].map((tenant) => [tenant.id, tenant])).values(),
  ]
  /**
   * @description How many backlog banks carry a lead rating.
   * @param {string} rating - Hot, Warm or Cold.
   * @returns {number} The count.
   */
  const rated = (rating: string) => backlog.filter((tenant) => tenant.leadRating === rating).length
  return {
    scope: 'ALL',
    total: backlog.length,
    hot: rated('Hot'),
    warm: rated('Warm'),
    cold: rated('Cold'),
    awaitingApproval: pending.length,
  }
}

/**
 * @description The My Page section, from the My Page summary.
 * @param {SalesUserRecord} caller - The signed-in user.
 * @returns {DashboardMyPage} The caller's own figures.
 */
export function myPageSection(caller: SalesUserRecord): DashboardMyPage {
  const summary = (summaryReply({} as AxiosRequestConfig)[1] as { data: MyPageSummary }).data
  return {
    scope: caller.role === 'Super Admin' ? 'ALL' : 'OWN',
    total: summary.total,
    achieved: summary.awaitingPayment + summary.completed,
    awaitingPayment: summary.awaitingPayment,
    atRisk: summary.atRisk,
    overdue: summary.overdue,
  }
}

/**
 * @description The Sales Team section: the caller's cohort.
 * @param {SalesUserRecord} caller - The signed-in user.
 * @returns {DashboardSalesTeam} The headcount by role, the inactive count and the banks covered.
 */
export function salesTeamSection(caller: SalesUserRecord): DashboardSalesTeam {
  const roster = listSalesRoster()
  const ids = resolveScopedUserIds(caller.id, caller.role, roster)
  const members = ids === null ? roster : roster.filter((user) => ids.includes(user.id))
  const owned = listTenants({ ownerIds: members.map((user) => user.id) })
  /**
   * @description How many of the caller's cohort hold a role.
   * @param {SalesUserRecord['role']} role - The role.
   * @returns {number} The count.
   */
  const count = (role: SalesUserRecord['role']) =>
    members.filter((user) => user.role === role).length
  return {
    scope: caller.role === 'Super Admin' || caller.role === 'Sales Head' ? 'ALL' : 'TEAM',
    members: members.length,
    heads: count('Sales Head'),
    managers: count('Sales Manager'),
    executives: count('Sales Executive'),
    inactive: members.filter((user) => user.status === 'Inactive').length,
    banksCovered: new Set(owned.map((tenant) => tenant.id)).size,
  }
}

/**
 * @description The User Management section: the whole roster.
 * @returns {DashboardUserManagement} The totals.
 */
export function userManagementSection(): DashboardUserManagement {
  const roster = listSalesRoster()
  const active = roster.filter((user) => user.status === 'Active').length
  return { scope: 'ALL', total: roster.length, active, inactive: roster.length - active }
}

/**
 * @description One month of the financial year to date.
 * @param {string} month - The month, yyyy-mm.
 * @param {{ endDate: string; achieved: boolean; date: string }[]} items - The targets in scope.
 * @returns {DashboardMonth} The month's figures.
 */
function monthOf(
  month: string,
  items: { endDate: string; achieved: boolean; date: string }[],
): DashboardMonth {
  const due = items.filter((item) => item.endDate.slice(0, 7) === month).length
  const achieved = items.filter(
    (item) => item.achieved && item.date.slice(0, 7) === month && item.endDate.slice(0, 7) >= month,
  ).length
  const lateAchieved = items.filter(
    (item) => item.achieved && item.date.slice(0, 7) === month && item.endDate.slice(0, 7) < month,
  ).length
  return {
    month,
    due,
    achieved,
    percent: due === 0 ? null : Math.min(100, Math.round((achieved / due) * 100)),
    lateAchieved,
    latePercent: 0,
  }
}

/**
 * @description The Achievement and Needs Attention sections for the caller's scope.
 * @param {SalesUserRecord} caller - The signed-in user.
 * @param {SalesUserRecord[]} people - The sellers in the caller's scope.
 * @returns {{ achievement: DashboardAchievement; needsAttention: DashboardNeedsAttention }} Both.
 */
export function performanceSections(
  caller: SalesUserRecord,
  people: SalesUserRecord[],
): { achievement: DashboardAchievement; needsAttention: DashboardNeedsAttention } {
  const today = indiaToday()
  const { period } = periodOf('FY', today)
  const ids = new Set(people.map((user) => user.id))
  const targets = targetsFor(ids, period, true)
  const inYear = targets.filter((item) => item.date >= period.startDate)
  const items = inYear.map((item) => ({
    endDate: item.target.endDate,
    achieved: item.achieved,
    date: item.date,
  }))
  const months = monthsOf(period, today).map((month) => monthOf(month, items))
  const finished = months.filter((m) => m.month < today.slice(0, 7) && m.percent !== null)
  const lowest = finished.reduce<DashboardMonth | null>(
    (worst, m) => (worst === null || (m.percent ?? 0) < (worst.percent ?? 0) ? m : worst),
    null,
  )
  const achieved = inYear.filter((item) => item.achieved).length
  const watched = targets
    .filter(
      (item) =>
        !item.achieved && ['OVERDUE', 'AT_RISK'].includes(pendingStatusOf(item.target, today)),
    )
    .map((item) => ({
      targetId: item.target.id,
      bank: item.tenant.name,
      owner: {
        userId: item.target.ownerId,
        name: people.find((user) => user.id === item.target.ownerId)?.name ?? '',
      },
      dueDate: item.target.endDate,
      status: pendingStatusOf(item.target, today) as 'OVERDUE' | 'AT_RISK',
    }))
    .sort(
      (a, b) =>
        Number(b.status === 'OVERDUE') - Number(a.status === 'OVERDUE') ||
        a.dueDate.localeCompare(b.dueDate) ||
        a.bank.localeCompare(b.bank),
    )
  const scope = scopeKindOf(caller)
  return {
    achievement: {
      scope,
      assigned: inYear.length,
      achieved,
      percent: inYear.length === 0 ? 0 : Math.round((achieved / inYear.length) * 100),
      lowestMonth:
        lowest && (lowest.percent ?? 100) < LOWEST_MONTH_PERCENT
          ? { month: lowest.month, percent: lowest.percent ?? 0 }
          : null,
      months,
    },
    needsAttention: { scope, count: watched.length, items: watched.slice(0, ATTENTION_CEILING) },
  }
}
